import { MY_BOOKED_ORDERS } from '../salesExec/orders.js';
import { TEAM_ORDERS } from '../salesSupervisor/orders.js';
import { VALIDATION_QUEUE } from '../telecaller/orders.js';
import { WAREHOUSE_ORDERS } from '../warehouse/orders.js';
import { FULFILMENT, STAGES, STAGE } from './orderModel.js';
import { computeLineValue } from './products.js';

// Point: orders now need to flow through every role as the SAME object, not
// four independently-seeded datasets that happened to look similar. This
// merges all four into one pool and normalizes every order/line item to the
// full union of fields used at any stage of the lifecycle — so a brand new
// order raised by the MR carries every field Warehouse or Telecaller might
// later set on it, not just the fields Sales Exec's screen originally cared
// about.
const TC_VALIDATION_IDX = STAGES.indexOf(STAGE.TELECALLER_VALIDATION);

function normalizeLineItem(li) {
  return {
    productSku: li.productSku, productName: li.productName, quantity: li.quantity,
    unitPrice: li.unitPrice, schemeApplied: li.schemeApplied,
    freeQuantity: li.freeQuantity || 0, totalQuantity: li.totalQuantity || li.quantity,
    lineValue: li.lineValue,
    allocated: li.allocated ?? false,
    fulfilmentStatus: li.fulfilmentStatus || FULFILMENT.PENDING,
    fulfilledQty: li.fulfilledQty ?? null,
    warehouseFulfilmentStatus: li.warehouseFulfilmentStatus || FULFILMENT.PENDING,
    warehouseAllocatedQty: li.warehouseAllocatedQty ?? null,
  };
}

// Backfill: any seeded order that has genuinely moved PAST Telecaller
// Validation must have gone through that gate, so it cannot legitimately
// carry a blank fulfilledQty — that was a hand-authoring gap, not a real
// "not yet processed" state. Defaults to full fulfilment (these are all
// happy-path historical orders in the backstory; none were authored with a
// deliberate partial story), and recomputes the scheme the same way the
// live telecallerSetFulfilment action does, so the numbers stay consistent
// rather than being copy-pasted from whatever the order was authored with.
function backfillFulfilment(order) {
  const stageIdx = STAGES.indexOf(order.currentStage);
  if (stageIdx <= TC_VALIDATION_IDX) return; // still at or before the gate — blank is correct
  order.lineItems.forEach(li => {
    if (li.fulfilledQty === null) {
      li.fulfilledQty = li.quantity;
      li.fulfilmentStatus = FULFILMENT.FULL;
      const recalced = computeLineValue(li.productSku, li.quantity);
      li.freeQuantity = recalced.freeQuantity;
      li.totalQuantity = recalced.totalQuantity;
      li.lineValue = recalced.value;
    }
    // Also backfill Warehouse's own number for lines it has already
    // allocated in the backstory (allocated: true) but never got a
    // warehouseAllocatedQty — same reasoning, same default.
    if (li.allocated && li.warehouseAllocatedQty === null) {
      li.warehouseAllocatedQty = li.fulfilledQty;
      li.warehouseFulfilmentStatus = FULFILMENT.FULL;
    }
  });
  order.orderValue = Math.round(order.lineItems.reduce((s, li) => s + li.lineValue, 0) * 100) / 100;
}

function normalizeOrder(o, defaultExecName) {
  const order = {
    orderNo: o.orderNo,
    customerId: o.customerId,
    pharmacyName: o.pharmacyName,
    execName: o.execName || defaultExecName,
    territory: o.territory || null,
    category: o.category || null,
    orderDate: o.orderDate,
    lineItems: o.lineItems.map(normalizeLineItem),
    orderValue: o.orderValue,
    currentStage: o.currentStage,
    statusHistory: o.statusHistory,
    comments: o.comments || [],
    otpSentTo: o.otpSentTo || null,
    otpCode: o.otpCode || null,
    otpVerified: o.otpVerified ?? false,
    approvedBy: o.approvedBy || null,
    trafficLight: o.trafficLight || 'Green',
    lrNumber: o.lrNumber || null,
    cancelledBy: o.cancelledBy || null,
    cancelReason: o.cancelReason || null,
  };
  backfillFulfilment(order);
  return order;
}

export const ALL_ORDERS = [
  ...MY_BOOKED_ORDERS.map(o => normalizeOrder(o, 'Tendai Moyo')),
  ...TEAM_ORDERS.map(o => normalizeOrder(o, o.execName)),
  ...VALIDATION_QUEUE.map(o => normalizeOrder(o, null)),
  ...WAREHOUSE_ORDERS.map(o => normalizeOrder(o, null)),
];
